"use client";

import { useState } from "react";

const DateRangePicker = ({ onApply }) => {
  const [selectedFilter, setSelectedFilter] = useState("custom_range");
  const [startDate, setStartDate] = useState(new Date(2025, 6, 9)); // initially 9 Jul 2025
  const [endDate, setEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 6)); // July 2025
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);

  const [isOpen, setIsOpen] = useState(false); // NEW: control visibility

  const dateFilters = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "last_7_days", label: "Last 7 Days" },
    { key: "last_30_days", label: "Last 30 Days" },
    { key: "this_month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
    { key: "custom_range", label: "Custom Range" },
  ];

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const formatDate = (date) => {
    if (!date) return "";
    const day = date.getDate().toString();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getPresetDates = (filterKey) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    switch (filterKey) {
      case "today":
        return { start: today, end: today };
      case "yesterday":
        return { start: yesterday, end: yesterday };
      case "last_7_days":
        const last7Start = new Date(today);
        last7Start.setDate(last7Start.getDate() - 6);
        return { start: last7Start, end: today };
      case "last_30_days":
        const last30Start = new Date(today);
        last30Start.setDate(last30Start.getDate() - 29);
        return { start: last30Start, end: today };
      case "this_month":
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: thisMonthStart, end: today };
      case "last_month":
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: lastMonthStart, end: lastMonthEnd };
      default:
        return { start: null, end: null };
    }
  };


   const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDay = new Date(year, month, 0 - (startingDayOfWeek - 1 - i));
      days.push({ date: prevMonthDay, isCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }

    const remainingCells = 42 - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      days.push({ date: new Date(year, month + 1, day), isCurrentMonth: false });
    }

    return days;
  };

   const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isDateSelected = (date) => {
    if (!startDate) return false;
    if (startDate && !endDate) {
      return date.toDateString() === startDate.toDateString();
    }
    return date.toDateString() === startDate.toDateString() || date.toDateString() === endDate?.toDateString();
  };


  const handleFilterSelect = (filterKey) => {
    setSelectedFilter(filterKey);
    if (filterKey !== "custom_range") {
      const { start, end } = getPresetDates(filterKey);
      setStartDate(start);
      setEndDate(end);
      setIsSelectingEnd(false);
    } else {
      setStartDate(null);
      setEndDate(null);
      setIsSelectingEnd(false);
    }
  };

  const handleDateClick = (date) => {
    if (selectedFilter !== "custom_range") return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
      setIsSelectingEnd(true);
    } else if (startDate && !endDate) {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(null);
      } else {
        setEndDate(date);
        setIsSelectingEnd(false);
      }
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const handleApply = () => {
    const result = {
      date_filter: selectedFilter,
      start_date: selectedFilter === "custom_range" && startDate ? formatDate(startDate) : "",
      end_date: selectedFilter === "custom_range" && endDate ? formatDate(endDate) : "",
    };
    onApply(result);
    setIsOpen(false); // close after apply
  };

  const handleCancel = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedFilter("custom_range");
    setIsSelectingEnd(false);
    setIsOpen(false); // close after cancel
  };

  const renderCalendar = (monthOffset = 0) => {
    const displayMonth = new Date(currentMonth);
    displayMonth.setMonth(displayMonth.getMonth() + monthOffset);
    const days = getDaysInMonth(displayMonth);


    
    return (
      <div style={styles.calendar}>
        <div style={styles.monthHeader}>
          {months[displayMonth.getMonth()]} {displayMonth.getFullYear()}
        </div>
        <div style={styles.daysGrid}>
          {daysOfWeek.map((day) => (
            <div key={day} style={styles.dayHeader}>{day}</div>
          ))}
        </div>
        <div style={styles.datesGrid}>
          {days.map((dayObj, index) => {
            const { date, isCurrentMonth } = dayObj;
            const isSelected = isDateSelected(date);
            const isInRange = isDateInRange(date);

            return (
              <button
                key={index}
                onClick={() => handleDateClick(date)}
                style={{
                  ...styles.dateButton,
                  ...(isSelected ? styles.selectedDate : {}),
                  ...(isInRange && !isSelected ? styles.rangeDate : {}),
                  ...(isCurrentMonth ? {} : styles.otherMonthDate),
                  cursor: selectedFilter !== "custom_range" ? "default" : "pointer",
                }}
                disabled={selectedFilter !== "custom_range"}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ✅ NEW: render compact pill or calendar
  return (
    <div>
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "6px 12px",
            cursor: "pointer",
            fontSize: "14px",
            color: "#374151",
          }}
        >
          {startDate ? formatDate(startDate) : "Select date"} <span role="img" aria-label="calendar">📅</span>
        </button>
      ) : (
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.sidebar}>
              {dateFilters.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => handleFilterSelect(filter.key)}
                  style={{
                    ...styles.filterButton,
                    ...(selectedFilter === filter.key ? styles.activeFilter : {}),
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div style={styles.calendarSection}>
              <div style={styles.navigation}>
                <button onClick={() => navigateMonth(-1)} style={styles.navButton}>
                  &#8249;
                </button>
                <button onClick={() => navigateMonth(1)} style={styles.navButton}>
                  &#8250;
                </button>
              </div>

              <div style={styles.calendarsContainer}>
                {renderCalendar(0)}
                {renderCalendar(1)}
              </div>

              <div style={styles.footer}>
                <div style={styles.dateDisplay}>
                  {startDate && endDate
                    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                    : startDate
                    ? formatDate(startDate)
                    : "Select date range"}
                </div>
                <div style={styles.buttonGroup}>
                  <button onClick={handleCancel} style={styles.cancelButton}>
                    Cancel
                  </button>
                  <button onClick={handleApply} style={styles.applyButton}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    padding: "16px",
    width: "70%",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  content: {
    display: "flex",
    gap: "16px",
  },
  sidebar: {
    width: "128px",
    borderRight: "1px solid #e5e7eb",
    paddingRight: "16px",
    display: "flex",
    flexDirection: "column",
  },
  filterButton: {
    width: "100%",
    textAlign: "left",
    padding: "4px 8px",
    fontSize: "14px",
    borderRadius: "4px",
    marginBottom: "4px",
    border: "none",
    backgroundColor: "transparent",
    color: "#4b5563",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  activeFilter: {
    backgroundColor: "#3b82f6",
    color: "white",
  },
  calendarSection: {
    flex: 1,
  },
  navigation: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  navButton: {
    padding: "4px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    color: "#4b5563",
  },
  calendarsContainer: {
    display: "flex",
    gap: "32px",
  },
  calendar: {
    flex: 1,
  },
  monthHeader: {
    textAlign: "center",
    fontWeight: "500",
    marginBottom: "16px",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
    marginBottom: "8px",
  },
  dayHeader: {
    textAlign: "center",
    fontSize: "14px",
    color: "#6b7280",
    padding: "8px 0",
  },
  datesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
  },
  dateButton: {
    height: "32px",
    width: "32px",
    fontSize: "14px",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "transparent",
    color: "#374151",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  selectedDate: {
    backgroundColor: "#3b82f6",
    color: "white",
  },
  rangeDate: {
    backgroundColor: "#dbeafe",
  },
  otherMonthDate: {
    color: "#d1d5db",
  },
  footer: {
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dateDisplay: {
    fontSize: "14px",
  },
  buttonGroup: {
    display: "flex",
    gap: "8px",
  },
  cancelButton: {
    padding: "4px 12px",
    fontSize: "14px",
    color: "#4b5563",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  applyButton: {
    padding: "4px 12px",
    fontSize: "14px",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
}


export default DateRangePicker;
